import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
    Box,
    Button,
    LinearProgress,
    Paper
} from '@mui/material';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import MoneyOffRoundedIcon from '@mui/icons-material/MoneyOffRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';
import PieChartRoundedIcon from '@mui/icons-material/PieChartRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import { useNavigate, useSearchParams } from 'react-router-dom';

import BiMetric from '../../components/biMetric';
import MonthRangePicker from '../../components/monthRangePicker';
import PageHeader from '../../components/pageHeader';
import {
    formatMonthParam,
    getCurrentMonthStart,
    getMonthsAgoStart,
    parseMonthParam
} from '../../utils/dateUtils';
import {
    formatInteger,
    formatPercentRatioTwoDecimals
} from '../../utils/numberUtils';
import {
    compactCurrency,
    formatCurrency
} from '../../utils/stringFormat';
import {
    buildFaturamentoComposition,
    buildInadimplenciaKpis,
    fetchInadimplenciaMes
} from './inadimplenciaData';
import '../css/biAluguelAccess.css';
import '../css/biInadimplencia.css';

const minMonth = new Date(2025, 0, 1);

const getMonthFromParam = (value) => (value ? parseMonthParam(value) : getMonthsAgoStart(3));

const FaturamentoCompositionChart = ({ items, total }) => {
    const [activeItem, setActiveItem] = useState(null);
    let offset = 0;

    return (
        <div className='bi-ge-inadimplencia-donut-card-content'>
            <div
                className='bi-ge-inadimplencia-pie-wrap'
                aria-label='Composição do faturamento por status'
                onMouseLeave={() => setActiveItem(null)}
            >
                <svg className='bi-ge-inadimplencia-pie' viewBox='0 0 120 120' role='img'>
                    <circle className='bi-ge-inadimplencia-pie-track' cx='60' cy='60' r='42' pathLength='100' />
                    {items.map((item) => {
                        const share = (item.value / total) * 100;
                        const slice = (
                            <circle
                                key={item.key}
                                className='bi-ge-inadimplencia-pie-slice'
                                cx='60'
                                cy='60'
                                r='42'
                                pathLength='100'
                                tabIndex='0'
                                style={{
                                    '--slice-color': item.color,
                                    '--slice-size': share,
                                    '--slice-offset': offset
                                }}
                                onBlur={() => setActiveItem(null)}
                                onFocus={() => setActiveItem(item)}
                                onMouseEnter={() => setActiveItem(item)}
                            />
                        );
                        offset += share;
                        return slice;
                    })}
                </svg>
                <div className='bi-ge-inadimplencia-pie-center'>
                    <span>Valor total</span>
                    <strong>{compactCurrency(total)}</strong>
                </div>

                {activeItem && (
                    <div className='bi-ge-inadimplencia-pie-tooltip'>
                        <span>{activeItem.label}</span>
                        <strong>{formatCurrency(activeItem.value)}</strong>
                        <small>{formatInteger(activeItem.count)} faturas</small>
                        <em>{formatPercentRatioTwoDecimals(activeItem.value / total)}</em>
                    </div>
                )}
            </div>

            <div className='bi-ge-inadimplencia-composition-legend'>
                {items.map((item) => (
                    <div key={item.key} className='bi-ge-inadimplencia-composition-item'>
                        <span style={{ '--item-color': item.color }} />
                        <p>{item.label}</p>
                        <strong>{formatCurrency(item.value)}</strong>
                        <small>{formatInteger(item.count)} faturas</small>
                        <em>{formatPercentRatioTwoDecimals(item.value / total)}</em>
                    </div>
                ))}
            </div>
        </div>
    );
};

const CompactKpiCard = ({
    amount,
    count,
    countLabel,
    icon: Icon,
    subtitle,
    title,
    value
}) => (
    <Paper className='bi-ge-inadimplencia-side-kpi'>
        <div className='bi-ge-inadimplencia-side-kpi-heading'>
            <span>
                <Icon fontSize='small' />
            </span>
            <p>{title}</p>
        </div>

        <strong>{value}</strong>
        <small>{subtitle}</small>
        <div className='bi-ge-inadimplencia-side-kpi-meta'>
            <span>{formatCurrency(amount)}</span>
            <span>{formatInteger(count)} {countLabel}</span>
        </div>
    </Paper>
);

const InadimplenciaBIGE = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [dataInicio, setDataInicio] = useState(() => getMonthFromParam(searchParams.get('data_inicio')));
    const [dataFim, setDataFim] = useState(() => getMonthFromParam(searchParams.get('data_fim')));
    const [inadimplencia, setInadimplencia] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const loadInadimplencia = useCallback(async (startDate, endDate) => {
        setIsLoading(true);
        const periodInadimplencia = await fetchInadimplenciaMes({
            dataInicio: formatMonthParam(startDate),
            dataFim: formatMonthParam(endDate)
        });
        setInadimplencia(periodInadimplencia);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        loadInadimplencia(dataInicio, dataFim);
    }, [dataFim, dataInicio, loadInadimplencia]);

    const kpis = useMemo(() => (
        inadimplencia ? buildInadimplenciaKpis(inadimplencia) : null
    ), [inadimplencia]);
    const faturamentoComposition = useMemo(() => (
        inadimplencia && kpis ? buildFaturamentoComposition(inadimplencia, kpis) : []
    ), [inadimplencia, kpis]);

    const handleDateRangeApply = (startDate, endDate) => {
        setDataInicio(startDate);
        setDataFim(endDate);
    };

    const handleListClick = () => {
        const dataInicioParam = formatMonthParam(dataInicio);
        const dataFimParam = formatMonthParam(dataFim);

        navigate(`/BICOB/inadimplencia/lista?data_inicio=${dataInicioParam}&data_fim=${dataFimParam}`);
    };

    return (
        <Box className='mainBox bi-aluguel-access-page bi-ge-distratos-page bi-ge-inadimplencia-page'>
            <PageHeader>Inadimplência</PageHeader>

            <section className='bi-aluguel-access-shell'>
                {isLoading && <LinearProgress className='bi-aluguel-access-loading' />}

                <div className='bi-ge-inadimplencia-summary-row'>
                    <div className='bi-ge-inadimplencia-controls'>
                        <div className='bi-aluguel-access-filters bi-ge-indications-filters bi-ge-distratos-period-filter bi-ge-inadimplencia-period-filter'>
                            <MonthRangePicker
                                startDate={dataInicio}
                                endDate={dataFim}
                                minDate={minMonth}
                                maxDate={getCurrentMonthStart()}
                                disabled={isLoading}
                                onApply={handleDateRangeApply}
                            />
                        </div>

                        <Button
                            className='bi-ge-inadimplencia-list-button'
                            variant='contained'
                            startIcon={<FormatListBulletedRoundedIcon />}
                            disabled={isLoading}
                            onClick={handleListClick}
                        >
                            Ver inadimplentes
                        </Button>
                    </div>

                    {inadimplencia && (
                        <div className='bi-aluguel-access-metrics bi-ge-indications-metrics bi-ge-inadimplencia-metrics'>
                            <BiMetric
                                icon={PaidRoundedIcon}
                                label='Total faturado'
                                value={formatCurrency(inadimplencia.total_faturado)}
                                subtitle={`${formatInteger(inadimplencia.quantidade_faturado)} faturas`}
                                tone='green'
                            />
                            <BiMetric
                                icon={MoneyOffRoundedIcon}
                                label='Total atrasado'
                                value={formatCurrency(inadimplencia.total_atrasado)}
                                subtitle={`${formatInteger(inadimplencia.quantidade_atrasado)} faturas`}
                                tone='blue'
                            />
                            <BiMetric
                                icon={AssignmentTurnedInRoundedIcon}
                                label='Total em acordo'
                                value={formatCurrency(inadimplencia.total_acordo)}
                                subtitle={`${formatInteger(inadimplencia.quantidade_acordo)} faturas`}
                            />
                            <BiMetric
                                icon={PercentRoundedIcon}
                                label='KPI atraso'
                                value={formatPercentRatioTwoDecimals(kpis.percentualAtrasadoValor)}
                                subtitle={`${formatPercentRatioTwoDecimals(kpis.percentualAtrasadoQuantidade)} das faturas`}
                                tone='navy'
                            />
                        </div>
                    )}
                </div>

                {inadimplencia && (
                    <>
                        <div className='bi-ge-inadimplencia-kpi-grid'>
                            <Paper className='bi-aluguel-access-panel bi-ge-inadimplencia-pie-panel'>
                                <div className='bi-aluguel-access-panel-heading'>
                                    <div>
                                        <p>Composição</p>
                                        <h2>Faturamento por status</h2>
                                    </div>
                                    <PieChartRoundedIcon />
                                </div>

                                <FaturamentoCompositionChart
                                    items={faturamentoComposition}
                                    total={inadimplencia.total_faturado}
                                />
                            </Paper>

                            <div className='bi-ge-inadimplencia-side-kpis'>
                                <CompactKpiCard
                                    amount={inadimplencia.total_atrasado}
                                    count={inadimplencia.quantidade_atrasado}
                                    countLabel='faturas'
                                    icon={MoneyOffRoundedIcon}
                                    subtitle='Do valor faturado'
                                    title='Atraso'
                                    value={formatPercentRatioTwoDecimals(kpis.percentualAtrasadoValor)}
                                />

                                <CompactKpiCard
                                    amount={inadimplencia.total_acordo}
                                    count={inadimplencia.quantidade_acordo}
                                    countLabel='faturas'
                                    icon={ReceiptLongRoundedIcon}
                                    subtitle='Do valor atrasado'
                                    title='Em acordo'
                                    value={formatPercentRatioTwoDecimals(kpis.percentualAcordoValor)}
                                />

                                <CompactKpiCard
                                    amount={inadimplencia.total_acordo}
                                    count={inadimplencia.quantidade_acordo}
                                    countLabel='faturas'
                                    icon={PercentRoundedIcon}
                                    subtitle='Das faturas atrasadas'
                                    title='Acordo qtd.'
                                    value={formatPercentRatioTwoDecimals(kpis.percentualAcordoQuantidade)}
                                />
                            </div>
                        </div>
                    </>
                )}
            </section>
        </Box>
    );
};

export default InadimplenciaBIGE;
